import { config } from '../appConfig'
import { Wallet } from '../providers/WalletProvider'
import { generateSignature } from './createHmac'

const ONRAMPER_URL = 'https://buy.onramper.com'
const RELEVANT_CRYPTOS = 'icp_icp,sol,btc,eth,usdt_ethereum,usdc_ethereum'

const getWalletAddresses = (wallet: Wallet) => {
  let wallets = [`icp_icp:${wallet.accountId}`]

  if (wallet.ethAddress) {
    wallets.push(`eth:${wallet.ethAddress}`)
  }

  if (wallet.solAddress) {
    wallets.push(`sol:${wallet.solAddress}`)
  }

  if (wallet.btcAddress) {
    wallets.push(`btc:${wallet.btcAddress}`)
  }

  return wallets.join(',')
}

// Provided by Onramper
// https://docs.onramper.com/docs/signing-widget-url

const arrangeStringAlphabetically = (inputString: string) => {
  // Parse the input string into an object
  const inputObject: { [key: string]: { [key: string]: string } } = {}
  inputString.split('&').forEach((pair) => {
    // Split each pair into key and value
    const [key, value] = pair.split('=')
    // Split the value into nested key-value pairs
    const nestedPairs = value.split(',')
    inputObject[key] = {} // Initialize the nested object for the key
    nestedPairs.forEach((nestedPair) => {
      // Split each nested pair into nested key and value
      const [nestedKey, nestedValue] = nestedPair.split(':')
      // Assign the nested key-value pair to the nested object
      inputObject[key][nestedKey] = nestedValue
    })
  })

  // Sort the keys of each nested object alphabetically
  for (const key in inputObject) {
    inputObject[key] = Object.fromEntries(Object.entries(inputObject[key]).sort())
  }

  // Sort the keys of the top-level object alphabetically
  const sortedKeys = Object.keys(inputObject).sort()
  const sortedObject: { [key: string]: { [key: string]: string } } = {}
  sortedKeys.forEach((key) => {
    sortedObject[key] = inputObject[key]
  })

  // Reconstruct the string from the sorted object
  let resultString = ''
  for (const key in sortedObject) {
    resultString += key + '=' // Append the key
    // Append nested key-value pairs, sorted alphabetically
    resultString += Object.entries(sortedObject[key])
      .map(([nestedKey, nestedValue]) => `${nestedKey}:${nestedValue}`)
      .join(',')
    resultString += '&' // Separate key-value pairs with '&'
  }
  resultString = resultString.slice(0, -1) // Remove the trailing '&'

  return resultString
}

const getOnramperParams = (wallets: string, signature: string, defaultCrypto: string) =>
  new URLSearchParams({
    apikey: config.fetchEnv('ONRAMPER_API_KEY'),
    onlyCryptos: RELEVANT_CRYPTOS,
    themeName: 'dark',
    containerColor: '15161b',
    primaryColor: '9a78ff',
    secondaryColor: '34343a',
    cardColor: '1e1e25',
    primaryTextColor: 'ffffff',
    secondaryTextColor: 'ffffff',
    primaryBtnTextColor: 'ffffff',
    borderRadius: '1',
    wgBorderRadius: '1.5',
    mode: 'buy,sell',
    sell_onlyCryptos: RELEVANT_CRYPTOS,
    sell_defaultCrypto: 'icp_icp',
    wallets,
    defaultCrypto,
    signature,
  }).toString()

export const getOnramperUrl = async (wallet: Wallet, defaultCrypto: string = 'icp_icp') => {
  const walletParams = getWalletAddresses(wallet)
  const signature = await generateSignature(
    config.fetchEnv('ONRAMPER_SIGN_KEY'),
    arrangeStringAlphabetically(`wallets=${walletParams}`)
  )
  const params = getOnramperParams(walletParams, signature, defaultCrypto)

  return [ONRAMPER_URL, '?', params].join('')
}
